import type { ChangeEvent, RefObject } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { FileIcon, UploadSimpleIcon, XIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useBackgroundJobs } from '@/context/background-jobs';
import { PRICELIST_UPLOAD_ACCEPT } from 'src/lib/pricelist-utils';
import { useInvalidatePricelistViews } from './components/usePricelistActions';
import { invalidatePricelistCaseQueries } from './components/pricelistCaseQueryInvalidation';
import { CompanyPicker } from '@/components/company/CompanyPicker';
import { useCompanyPicker } from '@/components/company/useCompanyPicker';
import { pricelistVendorName } from '@/components/company/companyPickerDisplay';
import { enqueuePricelistUploads } from './enqueuePricelistUploads';
import { usePricelistUploadFiles } from './usePricelistUploadFiles';

/**
 * The price change an upload lands on when it starts from a case page. The
 * server attaches the file in the same transaction that stores it and gives
 * it the case's vendor, so the vendor picker is replaced by the case's own.
 */
export interface PricelistUploadCaseContext {
  caseId: string;
  caseNumber: string | null;
  companyName: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected file (e.g. from the drag-and-drop area). */
  initialFile?: File | null;
  initialFiles?: readonly File[];
  /** Set when uploading onto a pricelist update case. */
  caseContext?: PricelistUploadCaseContext;
}

/**
 * Pick a file and a company, then hand the upload to the background tracker.
 *
 * The dialog closes as soon as the job starts rather than holding the user
 * through an extraction that can take minutes — the panel reports the rest, and
 * links to the finished pricelist.
 */
export function PricelistUploadModal({
  open,
  onOpenChange,
  initialFile,
  initialFiles,
  caseContext,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {caseContext ? (
              <T context="title of the pricelist upload modal when the file goes onto a price change">
                Upload to Price Change
              </T>
            ) : (
              <T context="title of the pricelist upload modal">
                Upload Pricelist
              </T>
            )}
          </DialogTitle>
        </DialogHeader>
        {/* Mount the form only while open so its state resets on every open —
            avoids a state-syncing effect. */}
        {open && (
          <PricelistUploadForm
            onOpenChange={onOpenChange}
            initialFile={initialFile ?? null}
            initialFiles={initialFiles ?? []}
            caseContext={caseContext}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PricelistUploadForm({
  onOpenChange,
  initialFile,
  initialFiles,
  caseContext,
}: {
  onOpenChange: (open: boolean) => void;
  initialFile: File | null;
  initialFiles: readonly File[];
  caseContext?: PricelistUploadCaseContext;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startJob } = useBackgroundJobs();
  const invalidatePricelistViews = useInvalidatePricelistViews();
  const picker = useCompanyPicker(null, { forPricelistManagement: true });
  const files = usePricelistUploadFiles({
    initialFile,
    initialFiles,
    caseMode: Boolean(caseContext),
  });

  /**
   * Hand the upload to the background tracker and close.
   *
   * Nothing is awaited: the job outlives this dialog, so the extraction finishes
   * whether or not the user stays on this page. The lists are invalidated from
   * inside the job for the same reason — the page that would have refetched may
   * be long gone by the time the server answers.
   */
  function handleSubmit() {
    if (files.filesToUpload.length === 0) return;
    // On a case the server assigns the case's vendor; sending one here would
    // only be refused if it disagreed.
    enqueuePricelistUploads({
      files: files.filesToUpload,
      startJob,
      companyId: caseContext ? undefined : picker.selectedCompany?.id,
      caseId: caseContext?.caseId,
      gt,
      viewDocument: (pdfDocumentId) =>
        void navigate({
          to: '/item-master/pricelist-management/$pdfDocumentId',
          params: { pdfDocumentId },
        }),
      invalidatePricelistViews,
      invalidateCaseViews: () => invalidatePricelistCaseQueries(queryClient),
    });
    onOpenChange(false);
  }

  return (
    <>
      <div className="flex flex-col gap-4 py-2 overflow-hidden">
        {caseContext ? (
          <PricelistUploadCaseBanner caseContext={caseContext} />
        ) : (
          <PricelistUploadVendorField picker={picker} />
        )}

        {/* File picker */}
        <PricelistUploadFileField
          caseMode={Boolean(caseContext)}
          fileInputRef={files.fileInputRef}
          selectedFile={files.selectedFile}
          selectedFiles={files.selectedFiles}
          sizeError={files.sizeError}
          onFileChange={files.handleFileChange}
          onRemoveFile={files.removeSelectedFile}
        />

        {/* No progress here on purpose: the upload is reported by the
            background-task panel, which outlives this dialog. */}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          <T context="cancel button in pricelist upload modal">Cancel</T>
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={files.filesToUpload.length === 0}
        >
          <T context="submit button label in pricelist upload modal">Upload</T>
        </Button>
      </DialogFooter>
    </>
  );
}

function PricelistUploadCaseBanner({
  caseContext,
}: {
  caseContext: PricelistUploadCaseContext;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">
        <T context="label above the price change an upload will be attached to">
          Price Change
        </T>
      </span>
      <p className="text-sm text-muted-foreground">
        {caseContext.caseNumber ?? ''}{' '}
        {pricelistVendorName(caseContext.companyName)}
      </p>
      <p className="text-xs text-muted-foreground">
        <T context="explanation under the price change label in the upload modal">
          The file is attached to this price change and takes its vendor.
          Outlook emails (.msg) are attached as notices.
        </T>
      </p>
    </div>
  );
}

function PricelistUploadVendorField({
  picker,
}: {
  picker: ReturnType<typeof useCompanyPicker>;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">
        <T context="label for vendor-number selector in pricelist upload modal">
          Vendor Number
        </T>
      </span>
      <CompanyPicker
        picker={picker}
        triggerPlaceholder={gt('Unassigned', {
          context: 'pricelist upload modal default vendor-number selection',
        })}
        searchPlaceholder={gt(
          'Search by vendor number, company name, or company code…',
          {
            context:
              'pricelist upload modal search placeholder covering 5-digit vendor number, name, and company code',
          },
        )}
        unassignedLabel={
          <T context="unassigned vendor option in pricelist upload modal">
            Unassigned
          </T>
        }
        emptyState={
          <T context="empty state when vendor-number search yields no results in upload modal">
            No vendor numbers found
          </T>
        }
      />
    </div>
  );
}

function PricelistUploadFileField({
  caseMode,
  fileInputRef,
  selectedFile,
  selectedFiles,
  sizeError,
  onFileChange,
  onRemoveFile,
}: {
  caseMode: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  selectedFile: File | null;
  selectedFiles: readonly { id: string; file: File }[];
  sizeError: string | null;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (id: string) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">
        <T context="label for file picker in pricelist upload modal">File</T>
      </span>
      <input
        ref={fileInputRef}
        type="file"
        accept={PRICELIST_UPLOAD_ACCEPT}
        multiple={caseMode}
        className="hidden"
        onChange={onFileChange}
      />
      <div className="flex items-center gap-3 min-w-0">
        <Button
          variant="outline"
          className="shrink-0"
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadSimpleIcon className="mr-2 h-4 w-4" />
          <T context="button to open file picker in pricelist upload modal">
            Choose File
          </T>
        </Button>
        {!caseMode && selectedFile && (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0">
            <FileIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">{selectedFile.name}</span>
          </span>
        )}
      </div>
      {caseMode && selectedFiles.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {selectedFiles.map((item) => (
            <li key={item.id} className="flex items-center gap-1.5 min-w-0">
              <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm text-muted-foreground min-w-0 flex-1">
                {item.file.name}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => onRemoveFile(item.id)}
                aria-label={gt('Remove {fileName}', {
                  context:
                    'button that removes one pending file from the price change upload dialog',
                  fileName: item.file.name,
                })}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {sizeError && <p className="text-sm text-destructive">{sizeError}</p>}
    </div>
  );
}
